import React, { createContext, useCallback, useEffect, useRef, useState, useMemo } from 'react';
import type { ComponentType } from 'react';
import debounce from 'lodash/debounce';

import { useNativeCollabState } from '@confluence/native-collab';

import type { SaveIndicatorContextType, SaveIndicatorContextProviderProps } from './types';

export const SaveIndicatorContext = createContext<SaveIndicatorContextType>({
	processNcsSaving: () => {},
	isSaving: false,
	savedTime: new Date(),
});

type CommitStatusPayload = {
	version: number;
	status: 'attempt' | 'success' | 'failure';
};

const DEBOUNCE_SAVE_PERIOD = 1200;
const COMMIT_STATUS_DEBOUNCE = 500;
const NCS_POLLING_INTERVAL = 500;

export const SaveIndicatorContextProvider = ({
	syncCollabEditProvider,
	isNcs,
	children,
}: SaveIndicatorContextProviderProps) => {
	const [isSaving, setIsSaving] = useState(false);
	const [savedTime, setSavedTime] = useState<Date>(new Date());
	const [animationFrameSavedId, setAnimationFrameSavedId] = useState<any>();
	const saveAttemptCallbackId = useRef<number>(0);
	const saveCompleteCallbackId = useRef<number>(0);
	const pollIntervalId = useRef<any>(0);

	const [{ collabEditProvider }] = useNativeCollabState();

	// These window functions are used here as performance efficient callback throttlers
	// requestIdleCallback is not supported by all browsers, hence fallback to requestAnimationFrame
	const throttledCallback = window.requestIdleCallback || window.requestAnimationFrame;

	const cancelThrottle = useCallback(
		(id: number) => {
			if (throttledCallback === window.requestIdleCallback) {
				window.cancelIdleCallback(id);
			} else {
				window.cancelAnimationFrame(id);
			}
		},
		[throttledCallback],
	);

	/** Begin Synchrony Code **/
	// Set save indicator to saved with debounce
	const _debouncedSaved = debounce(() => {
		cancelThrottle(animationFrameSavedId);
		const id = throttledCallback(() => {
			setIsSaving(false);
			setSavedTime(new Date());
		});
		setAnimationFrameSavedId(id);
	}, DEBOUNCE_SAVE_PERIOD);

	const processSaving = useCallback(() => {
		if (!isSaving) {
			setIsSaving(true);
			_debouncedSaved();
		}
	}, [isSaving, _debouncedSaved]);

	// Listen to Synchrony pings
	useEffect(() => {
		if (!isNcs && syncCollabEditProvider) {
			syncCollabEditProvider.on('entity', ({ entity }) => {
				entity.on('ack', (event) => {
					const hasPendingChanges = event?.pending?.length > 0;
					if (!hasPendingChanges) {
						processSaving();
					}
				});
			});

			return () => {
				syncCollabEditProvider.off('entity', ({ entity }) => {
					entity.off('ack', () => {});
				});
			};
		}
		//eslint-disable-next-line
	}, [isNcs, syncCollabEditProvider]);
	/** End Synchrony Code **/

	/** Begin NCS Code */
	const onSaveSuccess = useCallback(() => {
		if (saveCompleteCallbackId.current) {
			cancelThrottle(saveCompleteCallbackId.current);
		}
		const id = throttledCallback(() => {
			setSavedTime(new Date());
			setIsSaving(false);
			clearInterval(pollIntervalId.current);
			pollIntervalId.current = 0;
			saveCompleteCallbackId.current = 0;
		});
		saveCompleteCallbackId.current = id;
	}, [cancelThrottle, throttledCallback]);

	const onSaveAttempt = useCallback(
		(autoSuccess: boolean = false) => {
			let autoSuccessTimeoutId;
			if (!isSaving) {
				if (saveAttemptCallbackId.current) {
					cancelThrottle(saveAttemptCallbackId.current);
				}
				const attemptId = throttledCallback(() => {
					setIsSaving(true);
					saveAttemptCallbackId.current = 0;
					if (autoSuccess) {
						autoSuccessTimeoutId = setTimeout(onSaveSuccess, DEBOUNCE_SAVE_PERIOD);
					}
				});
				saveAttemptCallbackId.current = attemptId;
			}

			return () => {
				if (autoSuccessTimeoutId) {
					clearTimeout(autoSuccessTimeoutId);
				}
			};
		},
		[isSaving, cancelThrottle, throttledCallback, onSaveSuccess],
	);

	const poll = useCallback(() => {
		if (!collabEditProvider) {
			return;
		}
		const unconfirmedSteps = collabEditProvider.getUnconfirmedSteps();
		if (unconfirmedSteps && unconfirmedSteps.length > 0) {
			onSaveAttempt();
		} else {
			onSaveSuccess();
		}
	}, [collabEditProvider, onSaveAttempt, onSaveSuccess]);

	// Update polling interval callback with new poll() whenever it updates
	// Because setInterval callback does not track state changes (i.e. isSaving)
	useEffect(() => {
		if (pollIntervalId.current) {
			clearInterval(pollIntervalId.current);
			pollIntervalId.current = setInterval(poll, NCS_POLLING_INTERVAL);
		}
	}, [poll]);

	useEffect(() => {
		if (!isNcs || !collabEditProvider) {
			return;
		}

		const onCommitStatus = debounce(
			(payload: CommitStatusPayload) => {
				if (payload.status === 'attempt' && !pollIntervalId.current) {
					const id = setInterval(poll, NCS_POLLING_INTERVAL);
					pollIntervalId.current = id;
				}
			},
			COMMIT_STATUS_DEBOUNCE,
			{ leading: true, trailing: false },
		);

		collabEditProvider.on('commit-status', onCommitStatus);

		return () => {
			if (pollIntervalId.current) {
				clearInterval(pollIntervalId.current);
			}
			collabEditProvider.off('commit-status', onCommitStatus);
		}; // eslint-disable-next-line
	}, [collabEditProvider, isNcs]);

	/** End NCS Code */

	// Cancel any awaiting callbacks on unmount
	useEffect(() => {
		return () => {
			if (pollIntervalId.current) {
				clearInterval(pollIntervalId.current);
			}
			if (animationFrameSavedId) {
				cancelThrottle(animationFrameSavedId);
			}
			if (saveAttemptCallbackId.current) {
				cancelThrottle(saveAttemptCallbackId.current);
			}
			if (saveCompleteCallbackId.current) {
				cancelThrottle(saveCompleteCallbackId.current);
			}
		};
		// eslint-disable-next-line
	}, []);

	const saveIndicatorContextValue = useMemo(
		() => ({
			isSaving,
			savedTime,
			processNcsSaving: onSaveAttempt,
		}),
		[isSaving, savedTime, onSaveAttempt],
	);

	return (
		<SaveIndicatorContext.Provider value={saveIndicatorContextValue}>
			{children}
		</SaveIndicatorContext.Provider>
	);
};

export const withSaveIndicatorContext =
	<T extends unknown>(isNcs: boolean) =>
	(WrappedComponent: ComponentType<T>) =>
	(props) => {
		return (
			<SaveIndicatorContextProvider isNcs={isNcs}>
				<WrappedComponent {...props} />
			</SaveIndicatorContextProvider>
		);
	};
